/**
 * Moving an opportunity between stages, with both of the things that can stop
 * it handled inline rather than as errors.
 *
 * The flow, in the order the server evaluates it:
 *
 *  1. **Is this a win?** §6.2: advancing to `DESIGN_WIN` *or any stage above
 *     it* closes the commercial opportunity, so the request must carry a
 *     closure. "Or above" is load-bearing: §5.8 permits stage skips, and a
 *     deal moved straight to `PRODUCTION` was unarguably won at some point —
 *     leaving it `open` means the win is never counted anywhere, because
 *     §6.14 counts design wins from closures. The comparison is on
 *     `sort_order`, never on the code, which is unordered text (§5.1 [R3]).
 *  2. **Does the gate pass?** §6.1 returns `422 STAGE_GATE_FAILED` with the
 *     missing fields; the dialog fills them and the move is retried with the
 *     closure already in hand.
 *
 * The order matters: a win dialog shown *after* a gate failure would ask for
 * the reason code twice.
 */

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { useStages } from '@/lib/reference';
import { StageGateDialog, type StageGateFailure } from './StageGateDialog';
import { DesignWinDialog, type DesignWinClosure } from './DesignWinDialog';

type Row = Record<string, unknown>;

interface PendingMove {
  row: Row;
  stageCode: string;
  closure?: DesignWinClosure;
}

export interface StageMover {
  /** Attempt a move. Opens whichever dialog the server would otherwise refuse over. */
  move: (row: Row, stageCode: string) => void;
  dialogs: React.ReactNode;
  isPending: boolean;
}

export function useStageMove(onMoved?: () => void): StageMover {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const { byCode } = useStages();

  const [pending, setPending] = useState<PendingMove | null>(null);
  const [gate, setGate] = useState<{ move: PendingMove; failure: StageGateFailure } | null>(null);
  const [win, setWin] = useState<PendingMove | null>(null);
  const [busy, setBusy] = useState(false);

  const send = useCallback(
    async (attempt: PendingMove): Promise<void> => {
      setBusy(true);
      try {
        await api(`/api/opportunities/${String(attempt.row['id'])}/stage`, {
          method: 'POST',
          body: {
            stage_code: attempt.stageCode,
            ...(attempt.closure === undefined ? {} : { closure: attempt.closure }),
          },
        });
        setGate(null);
        setWin(null);
        setPending(null);
        void queryClient.invalidateQueries();
        onMoved?.();
      } catch (error) {
        if (error instanceof ApiError && error.code === 'STAGE_GATE_FAILED') {
          setGate({ move: attempt, failure: error.details as StageGateFailure });
        } else {
          push({
            tone: 'error',
            title: error instanceof ApiError ? error.message : 'The move was refused.',
          });
          setPending(null);
        }
      } finally {
        setBusy(false);
      }
    },
    [onMoved, push, queryClient],
  );

  const move = useCallback(
    (row: Row, stageCode: string) => {
      const target = byCode.get(stageCode);
      const designWin = byCode.get('DESIGN_WIN');
      const attempt: PendingMove = { row, stageCode };

      // "Or above it" — see the module comment. `sort_order`, never the code.
      const winning =
        target !== undefined &&
        designWin !== undefined &&
        target.sort_order >= designWin.sort_order &&
        row['status'] !== 'won';

      if (winning) {
        setWin(attempt);
        return;
      }
      void send(attempt);
    },
    [byCode, send],
  );

  const dialogs = (
    <>
      {win !== null && (
        <DesignWinDialog
          opportunity={win.row}
          targetStageName={byCode.get(win.stageCode)?.name ?? win.stageCode}
          busy={busy}
          onClose={() => setWin(null)}
          onConfirm={(closure) => {
            const attempt = { ...win, closure };
            setWin(null);
            setPending(attempt);
            void send(attempt);
          }}
        />
      )}

      {gate !== null && (
        <StageGateDialog
          opportunityId={String(gate.move.row['id'])}
          accountId={(gate.move.row['account_id'] as string | undefined) ?? null}
          failure={gate.failure}
          onClose={() => {
            setGate(null);
            setPending(null);
          }}
          onFilled={() => {
            // Retry with the closure already collected, so the win reason is
            // never asked for twice.
            const attempt = gate.move;
            setGate(null);
            void send(attempt);
          }}
        />
      )}
    </>
  );

  return { move, dialogs, isPending: busy || pending !== null };
}
